import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { fmtDatum } from '../utils/datum';

const PAUZE_START = 12 * 60 + 30;
const PAUZE_EIND = 13 * 60;

function tijdNaarMin(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}
function berekenUrenMetPauze(start, eind) {
  if (!start || !eind) return 0;
  const s = tijdNaarMin(start);
  const e = tijdNaarMin(eind);
  if (e <= s) return 0;
  const bruto = e - s;
  const pauzeTrekt = s < PAUZE_START && e > PAUZE_EIND;
  const pauzeOverlap = pauzeTrekt ? Math.min(e, PAUZE_EIND) - Math.max(s, PAUZE_START) : 0;
  return Math.round((bruto - Math.max(0, pauzeOverlap)) / 60 * 100) / 100;
}
function maakWhatsAppLink(telefoon, bericht) {
  const encoded = encodeURIComponent(bericht);
  if (!telefoon) return `https://wa.me/?text=${encoded}`;
  const nr = telefoon.replace(/\D/g, '').replace(/^0/, '31');
  return `https://wa.me/${nr}?text=${encoded}`;
}
function PlanBadge({ status }) {
  const cfg = {
    goedgekeurd: { bg: '#dff6dd', c: '#107C10', t: '✅ Goedgekeurd' },
    pending:     { bg: '#fff4ce', c: '#7A6400', t: '⏳ Wacht bevestiging' },
    ingepland:   { bg: '#f0f6ff', c: '#0078D4', t: '📅 Ingepland' },
  }[status] || { bg: '#f3f2f1', c: '#605e5c', t: status || 'Ingepland' };
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10,
    background: cfg.bg, color: cfg.c, whiteSpace: 'nowrap' }}>{cfg.t}</span>;
}

export default function FlexDashboard({ embedded = false }) {
  const {
    currentUser, isAdmin, isManager, isFlexkracht, canManage,
    medewerkers, flexPlanning, flexUren, flexUitbetalingen, flexSaldo,
    planFlexkracht, verwijderFlexPlanning, wijzigFlexPlanning,
    registreerFlexUren, wijzigFlexUren, betaalFlexUren,
    voegMedewerkerToe, werkMedewerkerBij, stuurNotificatie,
  } = useApp();

  const vandaag = new Date().toISOString().split('T')[0];
  const currentYear = new Date().getFullYear();
  const flexkrachten = medewerkers.filter(m => m.Rol === 'flexkracht');

  const [tab, setTab] = useState(isFlexkracht && !canManage ? 'mijnuren' : canManage ? 'planning' : 'overzicht');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);

  // Forms
  const [planForm, setPlanForm] = useState({ flexkrachtId: '', datum: '', startTijd: '', eindTijd: '', omschrijving: '', directGoedkeuren: false, stuurWA: false });
  const [urenForm, setUrenForm] = useState({ datum: vandaag, startTijd: '', eindTijd: '', omschrijving: '' });
  const [nieuwFk, setNieuwFk] = useState(null); // null = hidden
  const [nieuwFkErr, setNieuwFkErr] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [notifyEdit, setNotifyEdit] = useState(false);

  // AUTO-CHECKOUT: elke minuut checken of er open klokken zijn na 17:30
  const autoCheckout = useCallback(async () => {
    const now = new Date();
    if (now.getHours() < 17 || (now.getHours() === 17 && now.getMinutes() < 30)) return;
    const openVandaag = flexUren.filter(u => u.isOpenKlok && u.datum === vandaag);
    for (const u of openVandaag) {
      try {
        await wijzigFlexUren(u.id, {
          eindTijd: '17:00', uren: berekenUrenMetPauze(u.startTijd, '17:00'),
          isOpenKlok: false, autoUitgeklokt: true,
          autoTijdstip: now.toTimeString().slice(0, 5),
        });
      } catch {}
    }
  }, [flexUren, vandaag, wijzigFlexUren]);

  useEffect(() => {
    autoCheckout();
    const t = setInterval(autoCheckout, 60000);
    return () => clearInterval(t);
  }, [autoCheckout]);

  // Computed
  const mijnPlanning = isFlexkracht
    ? [...flexPlanning].filter(p => p.flexkrachtId === currentUser?.id).sort((a,b) => b.datum.localeCompare(a.datum))
    : [];
  const mijnUren = isFlexkracht
    ? [...flexUren].filter(u => u.flexkrachtId === currentUser?.id).sort((a,b) => b.datum.localeCompare(a.datum))
    : [];
  const openKlok = mijnUren.find(u => u.isOpenKlok);

  const flexSaldi = useMemo(() => flexkrachten.map(fk => {
    const inp  = flexPlanning.filter(p => p.flexkrachtId === fk.Id && new Date(p.datum).getFullYear() === currentYear).reduce((s,p) => s+(p.uren||0), 0);
    const gew  = flexUren.filter(u => u.flexkrachtId === fk.Id && !u.isOpenKlok && new Date(u.datum).getFullYear() === currentYear).reduce((s,u) => s+(u.uren||0), 0);
    const uit  = flexUitbetalingen.filter(u => u.flexkrachtId === fk.Id && new Date(u.datum).getFullYear() === currentYear).reduce((s,u) => s+(u.uren||0), 0);
    return { ...fk, inp, gew, uit, open: gew - uit };
  }), [flexkrachten, flexPlanning, flexUren, flexUitbetalingen, currentYear]);

  // Helpers
  const msg = (txt) => { setOk(txt); setErr(''); setTimeout(() => setOk(''), 4000); };
  const planUren = berekenUrenMetPauze(planForm.startTijd, planForm.eindTijd);
  const editUren = berekenUrenMetPauze(editForm.startTijd, editForm.eindTijd);
  const selFk = flexkrachten.find(f => f.Id === planForm.flexkrachtId);

  // ---- Handlers ----
  const doInklokken = async () => {
    if (!urenForm.datum || !urenForm.startTijd) { setErr('Vul datum en starttijd in.'); return; }
    if (openKlok) { setErr('Al ingeklokt — klok eerst uit.'); return; }
    setBusy(true);
    try {
      await registreerFlexUren({ datum: urenForm.datum, startTijd: urenForm.startTijd, eindTijd: null, uren: 0, omschrijving: urenForm.omschrijving, status: 'ingediend' });
      setUrenForm(f => ({ ...f, startTijd: '', eindTijd: '', omschrijving: '' }));
      msg('Ingeklokt! Vergeet niet uit te klokken.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const doUitklokken = async () => {
    if (!openKlok || !urenForm.eindTijd) { setErr('Vul eindtijd in.'); return; }
    const uren = berekenUrenMetPauze(openKlok.startTijd, urenForm.eindTijd);
    if (uren <= 0) { setErr('Eindtijd moet na starttijd liggen.'); return; }
    setBusy(true);
    try {
      await wijzigFlexUren(openKlok.id, { eindTijd: urenForm.eindTijd, uren, isOpenKlok: false });
      setUrenForm(f => ({ ...f, eindTijd: '', omschrijving: '' }));
      msg(`✅ Uitgeklokt — ${uren}u geregistreerd.`);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const doUren = async () => {
    if (!urenForm.datum || !urenForm.startTijd || !urenForm.eindTijd) { setErr('Vul alle velden in.'); return; }
    if (openKlok) { setErr('Sluit eerst de open klok.'); return; }
    const uren = berekenUrenMetPauze(urenForm.startTijd, urenForm.eindTijd);
    if (uren <= 0) { setErr('Eindtijd moet na starttijd liggen.'); return; }
    setBusy(true);
    try {
      await registreerFlexUren({ datum: urenForm.datum, startTijd: urenForm.startTijd, eindTijd: urenForm.eindTijd, uren, omschrijving: urenForm.omschrijving, status: 'ingediend' });
      setUrenForm({ datum: vandaag, startTijd: '', eindTijd: '', omschrijving: '' });
      msg(`${uren}u opgeslagen.`);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const doPlan = async () => {
    if (!planForm.flexkrachtId || !planForm.datum || !planForm.startTijd || !planForm.eindTijd) {
      setErr('Vul alle verplichte velden in.'); return;
    }
    const uren = planUren || 0;
    const status = planForm.directGoedkeuren ? 'goedgekeurd' : 'pending';
    setBusy(true);
    try {
      await planFlexkracht({ ...planForm, flexkrachtNaam: selFk?.Naam, uren, planStatus: status });
      if (planForm.stuurWA) {
        const d = new Date(planForm.datum + 'T00:00').toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
        const afzender = currentUser?.Naam?.split(' ')[0] || 'Slaaploods';
        const waMsg = `Hoi ${selFk.Naam.split(' ')[0]}! 👋\n\n` +
          `Je bent *ingepland* voor een dienst:\n\n` +
          `📅 *${d}*\n` +
          `🕐 ${planForm.startTijd} – ${planForm.eindTijd} (${uren}u)\n` +
          (planForm.omschrijving ? `📝 ${planForm.omschrijving}\n` : '') +
          `\nKun je dit bevestigen? 👍\n\n` +
          `Groet, ${afzender} — Slaaploods.nl`;
        window.open(maakWhatsAppLink(selFk?.Telefoon, waMsg), '_blank');
        // Altijd ook in-app notificatie sturen
        stuurNotificatie?.({ ontvangerIds: [planForm.flexkrachtId], type: 'flex_ingepland',
          titel: 'Nieuwe inplanning', bericht: `Je bent ingepland op ${d} van ${planForm.startTijd} tot ${planForm.eindTijd} (${uren}u).${planForm.omschrijving ? ' ' + planForm.omschrijving : ''}`, link: '/flex' });
      }
      setPlanForm({ flexkrachtId: '', datum: '', startTijd: '', eindTijd: '', omschrijving: '', directGoedkeuren: false, stuurWA: false });
      msg(`${selFk?.Naam} ingepland op ${fmtDatum(planForm.datum)}.`);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const doPlanGoedkeuren = async (p) => {
    try {
      await wijzigFlexPlanning(p.id, { planStatus: 'goedgekeurd' });
      stuurNotificatie?.({ ontvangerIds: [p.flexkrachtId], type: 'flex_ingepland', titel: 'Planning bevestigd',
        bericht: `Je planning op ${fmtDatum(p.datum)} ${p.startTijd}–${p.eindTijd} is goedgekeurd.`, link: '/flex' });
      msg('Planning goedgekeurd.');
    } catch (e) { setErr(e.message); }
  };

  const doNieuwFkOpslaan = async () => {
    if (!nieuwFk?.naam || !nieuwFk?.email) { setNieuwFkErr('Naam en e-mail zijn verplicht.'); return; }
    setBusy(true);
    try {
      await voegMedewerkerToe({
        naam: nieuwFk.naam, email: nieuwFk.email, wachtwoord: nieuwFk.wachtwoord || '',
        rol: 'flexkracht', isManager: false, isAdmin: false, status: 'actief',
        afdeling: 'Flex', contractUren: 0, indienstDatum: vandaag,
        telefoon: nieuwFk.telefoon || '', pendingApproval: !isAdmin,
      });
      setNieuwFk(null); setNieuwFkErr('');
      msg(`${nieuwFk.naam} aangemaakt${!isAdmin ? ' — wacht op goedkeuring beheerder' : ''}.`);
    } catch (e) { setNieuwFkErr(e.message); } finally { setBusy(false); }
  };

  const doEditSave = async () => {
    if (!editItem) return;
    setBusy(true);
    try {
      const upd = { datum: editForm.datum, startTijd: editForm.startTijd, eindTijd: editForm.eindTijd, uren: editUren, omschrijving: editForm.omschrijving };
      if (editItem.type === 'uren') {
        await wijzigFlexUren(editItem.data.id, { ...upd, isOpenKlok: false });
        if (notifyEdit) {
          const fk = flexkrachten.find(f => f.Id === editItem.data.flexkrachtId);
            const afzender = currentUser?.Naam?.split(' ')[0] || 'Slaaploods';
            const waMsg = `Hoi ${(fk?.Naam || '').split(' ')[0]}! 📝\n\n` +
              `Je urenregistratie is *bijgewerkt*:\n\n` +
              `📅 ${fmtDatum(editForm.datum)}\n` +
              `🕐 ${editForm.startTijd} – ${editForm.eindTijd} (${editUren}u)\n\n` +
              `Groet, ${afzender} — Slaaploods.nl`;
          window.open(maakWhatsAppLink(fk?.Telefoon, waMsg), '_blank');
          // Altijd ook in-app notificatie
          stuurNotificatie?.({ ontvangerIds: [editItem.data.flexkrachtId], type: 'flex_gewijzigd',
            titel: 'Uren bijgewerkt', bericht: `Je uren op ${fmtDatum(editForm.datum)} zijn gewijzigd naar ${editForm.startTijd}–${editForm.eindTijd} (${editUren}u).`, link: '/flex' });
        }
      } else {
        await wijzigFlexPlanning(editItem.data.id, upd);
      }
      setEditItem(null); setNotifyEdit(false);
      msg('Opgeslagen.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // Tabs bepalen
  const tabs = [];
  if (isFlexkracht) {
    tabs.push({ k: 'mijnuren', l: '⏱️ Mijn uren' });
    tabs.push({ k: 'mijnplanning', l: '📅 Mijn planning' });
  }
  if (canManage) {
    tabs.push({ k: 'planning', l: '📅 Inplannen' });
    tabs.push({ k: 'flexkrachten', l: '⚡ Flexkrachten' });
    tabs.push({ k: 'alleuren', l: '⏱️ Alle uren' });
    tabs.push({ k: 'overzicht', l: '📊 Overzicht' });
  }

  return (
    <div style={S.wrap}>
      {/* Tab bar */}
      <div style={S.tabs} className="r-tabs r-flex-tabs">
        {tabs.map(t => (
          <button key={t.k} style={{ ...S.tab, ...(tab === t.k ? S.tabA : {}) }}
            onClick={() => { setTab(t.k); setErr(''); setOk(''); }}>{t.l}</button>
        ))}
      </div>

      {ok && <div style={S.success}>✅ {ok}</div>}
      {err && tab !== 'mijnuren' && <div style={S.errBanner}>⚠️ {err}</div>}

      {/* ══════════════════════════════════════════════
          FLEXKRACHT — MIJN UREN
      ══════════════════════════════════════════════ */}
      {tab === 'mijnuren' && isFlexkracht && (
        <div style={S.sec}>
          {/* Open klok banner */}
          {openKlok && (
            <div style={{ background: '#fff4ce', border: '2px solid #FFB900', borderRadius: 12, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#7A6400', marginBottom: 6 }}>
                ⏰ Ingeklokt sinds {openKlok.startTijd} — {fmtDatum(openKlok.datum)}
              </div>
              <div style={{ fontSize: 12, color: '#a19f9d', marginBottom: 14 }}>
                Niet uitgeklokt om 17:00? De sessie wordt automatisch om 17:30 afgesloten met eindtijd 17:00.
              </div>
              <div style={S.row} className="r-form-row">
                <div style={S.field}>
                  <label style={S.lbl}>Eindtijd</label>
                  <input type="time" style={S.inp} value={urenForm.eindTijd}
                    onChange={e => setUrenForm(f => ({ ...f, eindTijd: e.target.value }))} />
                </div>
              </div>
              {urenForm.eindTijd && (
                <div style={S.preview}>{berekenUrenMetPauze(openKlok.startTijd, urenForm.eindTijd)}u
                  {tijdNaarMin(openKlok.startTijd) < PAUZE_START && tijdNaarMin(urenForm.eindTijd) > PAUZE_EIND ? ' (pauze afgetrokken)' : ''}
                </div>
              )}
              {err && <div style={{ ...S.err, marginBottom: 12 }}>⚠️ {err}</div>}
              <button style={{ ...S.btn, background: '#107C10' }} onClick={doUitklokken}
                disabled={busy || !urenForm.eindTijd}>
                {busy ? 'Bezig...' : '✅ Uitklokken'}
              </button>
            </div>
          )}

          {/* Uren invoer */}
          {!openKlok && (
            <div style={S.card} className="r-card">
              <h3 style={S.title}>Uren registreren</h3>
              <div style={S.row} className="r-form-row">
                <div style={S.field}>
                  <label style={S.lbl}>Datum</label>
                  <input type="date" style={S.inp} value={urenForm.datum} max={vandaag}
                    onChange={e => setUrenForm(f => ({ ...f, datum: e.target.value }))} />
                </div>
                <div style={S.field}>
                  <label style={S.lbl}>Starttijd</label>
                  <input type="time" style={S.inp} value={urenForm.startTijd}
                    onChange={e => setUrenForm(f => ({ ...f, startTijd: e.target.value }))} />
                </div>
                <div style={S.field}>
                  <label style={S.lbl}>Eindtijd <span style={{ fontWeight: 400, color: '#a19f9d', fontSize: 11 }}>(leeg = later uitklokken)</span></label>
                  <input type="time" style={S.inp} value={urenForm.eindTijd}
                    onChange={e => setUrenForm(f => ({ ...f, eindTijd: e.target.value }))} />
                </div>
              </div>
              {urenForm.startTijd && urenForm.eindTijd && (
                <div style={S.preview}>
                  = {berekenUrenMetPauze(urenForm.startTijd, urenForm.eindTijd)}u
                  {tijdNaarMin(urenForm.startTijd) < PAUZE_START && tijdNaarMin(urenForm.eindTijd) > PAUZE_EIND ? ' (pauze 12:30–13:00 afgetrokken)' : ''}
                </div>
              )}
              <div style={S.field}>
                <label style={S.lbl}>Opmerking (optioneel)</label>
                <input style={S.inp} value={urenForm.omschrijving} placeholder="Wat heb je gedaan?"
                  onChange={e => setUrenForm(f => ({ ...f, omschrijving: e.target.value }))} />
              </div>
              {err && <div style={S.err}>⚠️ {err}</div>}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {urenForm.eindTijd
                  ? <button style={S.btn} onClick={doUren} disabled={busy}>
                      {busy ? 'Bezig...' : '💾 Uren opslaan'}
                    </button>
                  : <button style={{ ...S.btn, background: '#E86E1C' }} onClick={doInklokken}
                      disabled={busy || !urenForm.startTijd}>
                      {busy ? 'Bezig...' : '⏰ Inklokken (uitklokken later)'}
                    </button>
                }
              </div>
            </div>
          )}

          {/* Mijn saldo overzicht */}
          {flexSaldo && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }} className="r-stats">
              <div style={{ background: '#f0f6ff', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#605e5c', marginBottom: 4 }}>📅 Ingepland</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#0078D4' }}>{flexSaldo.ingepland}u</div>
              </div>
              <div style={{ background: '#dff6dd', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#605e5c', marginBottom: 4 }}>✅ Gewerkt</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#107C10' }}>{flexSaldo.gewerkt}u</div>
              </div>
              <div style={{ background: '#f3f0ff', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#605e5c', marginBottom: 4 }}>💰 Uitbetaald</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#8764B8' }}>{flexSaldo.uitbetaald}u</div>
              </div>
              <div style={{ background: flexSaldo.openstaand > 0 ? '#FFF4EE' : '#dff6dd', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#605e5c', marginBottom: 4 }}>⏳ Openstaand</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: flexSaldo.openstaand > 0 ? '#E86E1C' : '#107C10' }}>{flexSaldo.openstaand}u</div>
              </div>
            </div>
          )}

          {/* Mijn uren tabel */}
          <div style={S.card} className="r-card">
            <h3 style={S.title}>Mijn gewerkte uren</h3>
            {mijnUren.length === 0 ? <p style={S.empty}>Nog geen uren ingevoerd.</p> : (
              <div className="r-table-wrap">
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Datum</th>
                  <th style={S.th}>Tijden</th>
                  <th style={S.th}>Uren</th>
                  <th style={S.th}>Opmerking</th>
                  <th style={S.th}>Status</th>
                </tr></thead><tbody>
                  {mijnUren.map(u => (
                    <tr key={u.id} style={{ ...S.tr, background: u.isOpenKlok ? '#fffbf0' : '' }}>
                      <td style={S.td}>{fmtDatum(u.datum)}</td>
                      <td style={S.td}>{u.startTijd}{u.eindTijd ? `–${u.eindTijd}` : ' (open)'}</td>
                      <td style={S.td}>{u.isOpenKlok
                        ? <span style={{ color: '#FFB900', fontWeight: 700 }}>bezig…</span>
                        : <strong>{u.uren}u</strong>}</td>
                      <td style={S.td}>{u.omschrijving || '—'}</td>
                      <td style={S.td}>
                        {u.isOpenKlok ? <span style={S.badgeY}>⏰ Ingeklokt</span>
                          : u.autoUitgeklokt ? <span style={S.badgeGr} title={`Auto-beëindigd om ${u.autoTijdstip||'17:30'}`}>🤖 Auto-beëindigd</span>
                          : <span style={S.badgeG}>✅ Afgerond</span>}
                      </td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          FLEXKRACHT — MIJN PLANNING
      ══════════════════════════════════════════════ */}
      {tab === 'mijnplanning' && isFlexkracht && (
        <div style={S.sec}>
          <div style={S.card} className="r-card">
            <h3 style={S.title}>Mijn planning</h3>
            {mijnPlanning.length === 0 ? <p style={S.empty}>Geen inplanningen.</p> : (
              <div className="r-table-wrap">
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Datum</th><th style={S.th}>Tijden</th><th style={S.th}>Uren</th><th style={S.th}>Opmerking</th><th style={S.th}>Status</th>
                </tr></thead><tbody>
                  {mijnPlanning.map(p => (
                    <tr key={p.id} style={S.tr}>
                      <td style={S.td}>{fmtDatum(p.datum)}</td>
                      <td style={S.td}>{p.startTijd}–{p.eindTijd}</td>
                      <td style={S.td}><strong>{p.uren}u</strong></td>
                      <td style={S.td}>{p.omschrijving || '—'}</td>
                      <td style={S.td}><PlanBadge status={p.planStatus || 'ingepland'} /></td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          MANAGER/ADMIN — INPLANNEN
      ══════════════════════════════════════════════ */}
      {tab === 'planning' && canManage && (
        <div style={S.sec} className="r-flex-section">
          {/* Inplanformulier */}
          <div style={S.card} className="r-card">
            <h3 style={S.title}>Flexkracht inplannen</h3>
            {flexkrachten.length === 0 ? (
              <div style={S.empty}>
                Geen flexkrachten. Maak er eerst een aan via <strong>⚡ Flexkrachten</strong>.
              </div>
            ) : (
              <>
                <div style={S.row} className="r-form-row">
                  <div style={S.field}>
                    <label style={S.lbl}>Flexkracht *</label>
                    <select style={S.inp} value={planForm.flexkrachtId}
                      onChange={e => setPlanForm(f => ({ ...f, flexkrachtId: e.target.value }))}>
                      <option value="" disabled>— Kies —</option>
                      {flexkrachten.map(f => (
                        <option key={f.Id} value={f.Id}>
                          {f.Naam}{f.pendingApproval ? ' ⏳' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={S.field}>
                    <label style={S.lbl}>Datum *</label>
                    <input type="date" style={S.inp} value={planForm.datum}
                      onChange={e => setPlanForm(f => ({ ...f, datum: e.target.value }))} />
                  </div>
                </div>
                <div style={S.row} className="r-form-row">
                  <div style={S.field}>
                    <label style={S.lbl}>Starttijd *</label>
                    <input type="time" style={S.inp} value={planForm.startTijd}
                      onChange={e => setPlanForm(f => ({ ...f, startTijd: e.target.value }))} />
                  </div>
                  <div style={S.field}>
                    <label style={S.lbl}>Eindtijd *</label>
                    <input type="time" style={S.inp} value={planForm.eindTijd}
                      onChange={e => setPlanForm(f => ({ ...f, eindTijd: e.target.value }))} />
                  </div>
                </div>
                {planUren > 0 && (
                  <div style={S.preview}>= {planUren}u{tijdNaarMin(planForm.startTijd) < PAUZE_START && tijdNaarMin(planForm.eindTijd) > PAUZE_EIND ? ' (pauze afgetrokken)' : ''}</div>
                )}
                <div style={S.field}>
                  <label style={S.lbl}>Opmerking (optioneel)</label>
                  <input style={S.inp} value={planForm.omschrijving} placeholder="Werkzaamheden, locatie…"
                    onChange={e => setPlanForm(f => ({ ...f, omschrijving: e.target.value }))} />
                </div>

                {/* Opties blok */}
                <div style={{ background: '#f3f2f1', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#605e5c', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Opties</div>
                  <label style={S.checkLbl}>
                    <input type="checkbox" checked={planForm.directGoedkeuren}
                      onChange={e => setPlanForm(f => ({ ...f, directGoedkeuren: e.target.checked }))} />
                    ✅ Direct goedkeuren
                    <span style={{ color: '#a19f9d', marginLeft: 6, fontWeight: 400 }}>(anders: wacht op bevestiging flexkracht)</span>
                  </label>
                  <label style={{ ...S.checkLbl, marginTop: 8 }}>
                    <input type="checkbox" checked={planForm.stuurWA}
                      onChange={e => setPlanForm(f => ({ ...f, stuurWA: e.target.checked }))} />
                    💬 Informeren via WhatsApp + app-notificatie
                    {selFk?.Telefoon
                      ? <span style={{ color: '#107C10', marginLeft: 6, fontWeight: 400 }}>{selFk.Telefoon}</span>
                      : selFk ? <span style={{ color: '#E86E1C', marginLeft: 6, fontWeight: 400 }}>(geen nr — je kiest zelf de ontvanger)</span>
                      : null}
                  </label>
                </div>

                {err && <div style={S.err}>⚠️ {err}</div>}
                <button style={S.btn} onClick={doPlan}
                  disabled={busy || !planForm.flexkrachtId || !planForm.datum || !planForm.startTijd || !planForm.eindTijd}>
                  {busy ? 'Bezig...' : '📅 Inplannen'}
                </button>
              </>
            )}
          </div>

          {/* Pending planningen */}
          {flexPlanning.filter(p => p.planStatus === 'pending' || p.planStatus === 'ingepland').length > 0 && (
            <div style={S.card} className="r-card">
              <h3 style={S.title}>⏳ Wacht op bevestiging</h3>
              <div className="r-table-wrap">
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Flexkracht</th><th style={S.th}>Datum</th><th style={S.th}>Tijden</th><th style={S.th}>Uren</th><th style={S.th}>Acties</th>
                </tr></thead><tbody>
                  {flexPlanning
                    .filter(p => p.planStatus === 'pending' || p.planStatus === 'ingepland')
                    .sort((a, b) => a.datum.localeCompare(b.datum))
                    .map(p => {
                      const fk = flexkrachten.find(f => f.Id === p.flexkrachtId);
                      const afzender = currentUser?.Naam?.split(' ')[0] || 'Slaaploods';
                      const waBevestig = `Hoi ${(fk?.Naam || p.flexkrachtNaam || '').split(' ')[0]}! 👋\n\n` +
                        `Kun je de volgende dienst bevestigen?\n\n` +
                        `📅 *${fmtDatum(p.datum)}*\n` +
                        `🕐 ${p.startTijd} – ${p.eindTijd} (${p.uren}u)\n\n` +
                        `Laat het weten 👍👎\n\n` +
                        `Groet, ${afzender} — Slaaploods.nl`;
                      return (
                        <tr key={p.id} style={S.tr}>
                          <td style={S.td}><strong>{p.flexkrachtNaam}</strong></td>
                          <td style={S.td}>{fmtDatum(p.datum)}</td>
                          <td style={S.td}>{p.startTijd}–{p.eindTijd}</td>
                          <td style={S.td}>{p.uren}u</td>
                          <td style={S.td}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              <button style={S.okBtn} onClick={() => doPlanGoedkeuren(p)}>✅ Goedkeuren</button>
                              <a href={maakWhatsAppLink(fk?.Telefoon, waBevestig)} target="_blank" rel="noreferrer" style={S.waBtn}>
                                💬 WA{!fk?.Telefoon ? ' ↗' : ''}
                              </a>
                              <button style={S.iconBtn} onClick={() => { setEditItem({ type: 'planning', data: p }); setEditForm({ datum: p.datum, startTijd: p.startTijd, eindTijd: p.eindTijd, omschrijving: p.omschrijving || '' }); }}>✏️</button>
                              <button style={{ ...S.iconBtn, borderColor: '#fde7e9' }} onClick={async () => { if (window.confirm('Verwijderen?')) await verwijderFlexPlanning(p.id); }}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody></table>
              </div>
            </div>
          )}

          {/* Alle planningen */}
          <div style={S.card} className="r-card">
            <h3 style={S.title}>Alle inplanningen (laatste 50)</h3>
            {flexPlanning.length === 0 ? <p style={S.empty}>Geen inplanningen.</p> : (
              <div className="r-table-wrap">
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Flexkracht</th><th style={S.th}>Datum</th><th style={S.th}>Tijden</th><th style={S.th}>Uren</th><th style={S.th}>Status</th><th style={S.th}>Acties</th>
                </tr></thead><tbody>
                  {[...flexPlanning].sort((a,b) => b.datum.localeCompare(a.datum)).slice(0, 50).map(p => {
                    const fk = flexkrachten.find(f => f.Id === p.flexkrachtId);
                    const afzender = currentUser?.Naam?.split(' ')[0] || 'Slaaploods';
                    const waMsg = `Hoi ${(fk?.Naam || p.flexkrachtNaam || '').split(' ')[0]}! 📅\n\n` +
                      `Even een herinnering — je staat ingepland:\n\n` +
                      `📅 *${fmtDatum(p.datum)}*\n` +
                      `🕐 ${p.startTijd} – ${p.eindTijd} (${p.uren}u)\n` +
                      (p.omschrijving ? `📝 ${p.omschrijving}\n` : '') +
                      `\nGroet, ${afzender} — Slaaploods.nl`;
                    return (
                      <tr key={p.id} style={S.tr}>
                        <td style={S.td}><strong>{p.flexkrachtNaam}</strong></td>
                        <td style={S.td}>{fmtDatum(p.datum)}</td>
                        <td style={S.td}>{p.startTijd}–{p.eindTijd}</td>
                        <td style={S.td}>{p.uren}u</td>
                        <td style={S.td}><PlanBadge status={p.planStatus || 'ingepland'} /></td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <a href={maakWhatsAppLink(fk?.Telefoon, waMsg)} target="_blank" rel="noreferrer"
                              style={{ ...S.iconBtn, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              title={fk?.Telefoon ? `WhatsApp naar ${fk.Telefoon}` : 'WhatsApp — kies zelf ontvanger'}>💬</a>
                            <button style={S.iconBtn} onClick={() => { setEditItem({ type: 'planning', data: p }); setEditForm({ datum: p.datum, startTijd: p.startTijd, eindTijd: p.eindTijd, omschrijving: p.omschrijving || '' }); }}>✏️</button>
                            <button style={{ ...S.iconBtn, borderColor: '#fde7e9' }} onClick={async () => { if (window.confirm('Verwijderen?')) await verwijderFlexPlanning(p.id); }}>🗑️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody></table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          MANAGER/ADMIN — FLEXKRACHTEN BEHEER
      ══════════════════════════════════════════════ */}
      {tab === 'flexkrachten' && canManage && (
        <div style={S.sec}>
          {/* Pending goedkeuring banner (alleen admin ziet dit) */}
          {isAdmin && flexkrachten.filter(f => f.pendingApproval).length > 0 && (
            <div style={{ background: '#fff4ce', border: '1px solid #FFB900', borderRadius: 10, padding: '12px 16px' }}>
              <strong style={{ color: '#7A6400' }}>
                ⏳ {flexkrachten.filter(f => f.pendingApproval).length} flexkracht(en) wachten op jouw goedkeuring
              </strong>
              <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                Na goedkeuring kunnen ze inloggen en hun eigen uren bijhouden.
              </div>
            </div>
          )}

          <div style={S.card} className="r-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={S.title}>⚡ Flexkrachten</h3>
              {!nieuwFk && (
                <button style={S.btn} onClick={() => setNieuwFk({ naam: '', email: '', telefoon: '', wachtwoord: '' })}>
                  + Nieuwe flexkracht
                </button>
              )}
            </div>

            {/* Nieuw flexkracht formulier */}
            {nieuwFk && (
              <div style={{ background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#0078D4' }}>
                  Nieuwe flexkracht toevoegen
                </div>
                {!isAdmin && (
                  <div style={{ background: '#fff4ce', border: '1px solid #FFB900', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#7A6400' }}>
                    ℹ️ Wordt als <strong>tijdelijk</strong> aangemaakt — beheerder moet goedkeuren voor app-toegang. Inplannen kan al direct.
                  </div>
                )}
                <div style={S.row} className="r-form-row">
                  <div style={S.field}>
                    <label style={S.lbl}>Naam *</label>
                    <input style={S.inp} value={nieuwFk.naam} placeholder="Volledige naam"
                      onChange={e => setNieuwFk(f => ({ ...f, naam: e.target.value }))} />
                  </div>
                  <div style={S.field}>
                    <label style={S.lbl}>E-mail *</label>
                    <input style={S.inp} value={nieuwFk.email} placeholder="naam@email.nl"
                      onChange={e => setNieuwFk(f => ({ ...f, email: e.target.value }))} />
                  </div>
                </div>
                <div style={S.row} className="r-form-row">
                  <div style={S.field}>
                    <label style={S.lbl}>Telefoon <span style={{ fontWeight: 400, color: '#a19f9d' }}>(voor WhatsApp)</span></label>
                    <input style={S.inp} value={nieuwFk.telefoon} placeholder="06-12345678"
                      onChange={e => setNieuwFk(f => ({ ...f, telefoon: e.target.value }))} />
                  </div>
                  <div style={S.field}>
                    <label style={S.lbl}>Wachtwoord <span style={{ fontWeight: 400, color: '#a19f9d' }}>(app-toegang)</span></label>
                    <input type="password" style={S.inp} value={nieuwFk.wachtwoord}
                      placeholder="Min. 8 tekens (optioneel)"
                      onChange={e => setNieuwFk(f => ({ ...f, wachtwoord: e.target.value }))} />
                  </div>
                </div>
                {nieuwFkErr && <div style={S.err}>⚠️ {nieuwFkErr}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={S.btn} onClick={doNieuwFkOpslaan} disabled={busy}>
                    {busy ? 'Bezig...' : '💾 Opslaan'}
                  </button>
                  <button style={S.cancelBtn} onClick={() => { setNieuwFk(null); setNieuwFkErr(''); }}>Annuleren</button>
                </div>
              </div>
            )}

            {flexkrachten.length === 0 ? <p style={S.empty}>Nog geen flexkrachten.</p> : (
              <div className="r-table-wrap">
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Naam</th>
                  <th style={S.th}>Telefoon</th>
                  <th style={S.th}>E-mail</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Acties</th>
                </tr></thead><tbody>
                  {flexkrachten.map(fk => (
                    <tr key={fk.Id} style={{ ...S.tr, background: fk.pendingApproval ? '#fffbf0' : '' }}>
                      <td style={S.td}>
                        <strong>{fk.Naam}</strong>
                        {fk.pendingApproval && <span style={{ ...S.badgeY, marginLeft: 8 }}>⏳ Tijdelijk</span>}
                      </td>
                      <td style={S.td}>
                        {fk.Telefoon
                          ? <a href={`tel:${fk.Telefoon}`} style={{ color: '#0078D4', textDecoration: 'none' }}>{fk.Telefoon}</a>
                          : <span style={{ color: '#c8c6c4' }}>—</span>}
                      </td>
                      <td style={S.td} style={{ fontSize: 12, color: '#605e5c' }}>{fk.Email || '—'}</td>
                      <td style={S.td}>
                        {fk.pendingApproval
                          ? <span style={S.badgeY}>Wacht goedkeuring</span>
                          : <span style={S.badgeG}>Actief</span>}
                      </td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {isAdmin && fk.pendingApproval && (
                            <button style={S.okBtn}
                              onClick={() => werkMedewerkerBij(fk.Id, { pendingApproval: false })}>
                              ✅ Goedkeuren
                            </button>
                          )}
                          {fk.Telefoon && (
                            <a href={maakWhatsAppLink(fk.Telefoon, `Hoi ${fk.Naam.split(' ')[0]}, `)}
                              target="_blank" rel="noreferrer" style={{ ...S.iconBtn, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              title="WhatsApp openen">💬</a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          MANAGER/ADMIN — ALLE UREN
      ══════════════════════════════════════════════ */}
      {tab === 'alleuren' && canManage && (
        <div style={S.sec}>
          <div style={S.card} className="r-card">
            <h3 style={S.title}>Alle flexkracht uren</h3>
            {flexUren.length === 0 ? <p style={S.empty}>Geen uren geregistreerd.</p> : (
              <div className="r-table-wrap">
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Naam</th><th style={S.th}>Datum</th><th style={S.th}>Tijden</th>
                  <th style={S.th}>Uren</th><th style={S.th}>Opmerking</th><th style={S.th}>Status</th><th style={S.th}></th>
                </tr></thead><tbody>
                  {[...flexUren].sort((a,b) => b.datum.localeCompare(a.datum)).map(u => (
                    <tr key={u.id} style={{ ...S.tr, background: u.isOpenKlok ? '#fffbf0' : '' }}>
                      <td style={S.td}><strong>{u.flexkrachtNaam}</strong></td>
                      <td style={S.td}>{fmtDatum(u.datum)}</td>
                      <td style={S.td}>{u.startTijd}{u.eindTijd ? `–${u.eindTijd}` : ''}</td>
                      <td style={S.td}>{u.isOpenKlok ? <span style={{ color: '#FFB900' }}>⏰</span> : <strong>{u.uren}u</strong>}</td>
                      <td style={S.td}>{u.omschrijving || '—'}</td>
                      <td style={S.td}>
                        {u.isOpenKlok ? <span style={S.badgeY}>Ingeklokt</span>
                          : u.autoUitgeklokt ? <span style={S.badgeGr} title={`Auto om ${u.autoTijdstip||'17:30'}`}>🤖 Auto</span>
                          : <span style={S.badgeG}>Afgerond</span>}
                      </td>
                      <td style={S.td}>
                        <button style={S.iconBtn} onClick={() => {
                          setEditItem({ type: 'uren', data: u });
                          setEditForm({ datum: u.datum, startTijd: u.startTijd, eindTijd: u.eindTijd || '', omschrijving: u.omschrijving || '' });
                        }}>✏️</button>
                      </td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          OVERZICHT SALDI
      ══════════════════════════════════════════════ */}
      {tab === 'overzicht' && (
        <div style={S.sec}>
          <div style={S.card} className="r-card">
            <h3 style={S.title}>Flex urenoverzicht {currentYear}</h3>
            {flexSaldi.length === 0 ? <p style={S.empty}>Geen flexkrachten.</p> : (
              <div className="r-table-wrap">
                <table style={S.tbl}><thead><tr>
                  <th style={S.th}>Flexkracht</th><th style={S.th}>Ingepland</th>
                  <th style={S.th}>Gewerkt</th><th style={S.th}>Uitbetaald</th><th style={S.th}>Openstaand</th>
                </tr></thead><tbody>
                  {flexSaldi.map(fk => (
                    <tr key={fk.Id} style={S.tr}>
                      <td style={S.td}>
                        <strong>{fk.Naam}</strong>
                        {fk.pendingApproval && <span style={{ ...S.badgeY, marginLeft: 6, fontSize: 10 }}>⏳</span>}
                      </td>
                      <td style={S.td}>{fk.inp}u</td>
                      <td style={S.td}>{fk.gew}u</td>
                      <td style={S.td}>{fk.uit}u</td>
                      <td style={S.td}><strong style={{ color: fk.open > 0 ? '#E86E1C' : '#107C10' }}>{fk.open}u</strong></td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          EDIT MODAL
      ══════════════════════════════════════════════ */}
      {editItem && (
        <div style={S.overlay} onClick={() => setEditItem(null)}>
          <div style={S.modal} className="r-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
                {editItem.type === 'uren' ? '✏️ Uren wijzigen' : '✏️ Planning wijzigen'}
              </h3>
              <button style={S.closeBtn} onClick={() => { setEditItem(null); setNotifyEdit(false); }}>✕</button>
            </div>
            {editItem.data.flexkrachtNaam && (
              <div style={{ fontSize: 13, color: '#605e5c', marginBottom: 14, fontWeight: 500 }}>
                Flexkracht: <strong>{editItem.data.flexkrachtNaam}</strong>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '10px 12px', alignItems: 'center' }}>
              <label style={S.lbl}>Datum</label>
              <input type="date" style={S.inp} value={editForm.datum} onChange={e => setEditForm(f => ({ ...f, datum: e.target.value }))} />
              <label style={S.lbl}>Starttijd</label>
              <input type="time" style={S.inp} value={editForm.startTijd} onChange={e => setEditForm(f => ({ ...f, startTijd: e.target.value }))} />
              <label style={S.lbl}>Eindtijd</label>
              <input type="time" style={S.inp} value={editForm.eindTijd} onChange={e => setEditForm(f => ({ ...f, eindTijd: e.target.value }))} />
              <label style={S.lbl}>Opmerking</label>
              <input style={S.inp} value={editForm.omschrijving} onChange={e => setEditForm(f => ({ ...f, omschrijving: e.target.value }))} />
            </div>
            {editForm.startTijd && editForm.eindTijd && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0f6ff', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#0078D4' }}>
                {editUren}u berekend
                {tijdNaarMin(editForm.startTijd) < PAUZE_START && tijdNaarMin(editForm.eindTijd) > PAUZE_EIND
                  ? <span style={{ color: '#8764B8', fontWeight: 400, marginLeft: 8 }}>(pauze 12:30–13:00 afgetrokken)</span> : ''}
              </div>
            )}

            {/* Informeren opties — alleen voor manager/admin die uren van flexkracht wijzigt */}
            {editItem.type === 'uren' && canManage && (
              <div style={{ marginTop: 14, background: '#f3f2f1', borderRadius: 10, padding: '12px 14px' }}>
                <label style={S.checkLbl}>
                  <input type="checkbox" checked={notifyEdit} onChange={e => setNotifyEdit(e.target.checked)} />
                  Flexkracht informeren over wijziging
                </label>
                {notifyEdit && (() => {
                  const fk = flexkrachten.find(f => f.Id === editItem.data.flexkrachtId);
                  return (
                    <div style={{ fontSize: 11, color: '#605e5c', marginTop: 6, paddingLeft: 24 }}>
                      {fk?.Telefoon ? '💬 Bericht via WhatsApp wordt geopend na opslaan' : '🔔 Push-notificatie in de app (als flexkracht app-toegang heeft)'}
                    </div>
                  );
                })()}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button style={S.cancelBtn} onClick={() => { setEditItem(null); setNotifyEdit(false); }}>Annuleren</button>
              <button style={S.btn} onClick={doEditSave}
                disabled={busy || !editForm.startTijd || !editForm.eindTijd}>
                {busy ? 'Opslaan...' : '💾 Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  wrap:      { display: 'flex', flexDirection: 'column', gap: 24, maxWidth: '100%', boxSizing: 'border-box' },
  tabs:      { display: 'flex', gap: 8, borderBottom: '2px solid #edebe9', flexWrap: 'wrap' },
  tab:       { padding: '10px 20px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#605e5c', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2, whiteSpace: 'nowrap' },
  tabA:      { color: '#E86E1C', borderBottom: '2px solid #E86E1C' },
  sec:       { display: 'flex', flexDirection: 'column', gap: 20, maxWidth: '100%' },
  card:      { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', boxSizing: 'border-box', overflow: 'hidden' },
  title:     { fontSize: 18, fontWeight: 600, color: '#1a1a2e', marginBottom: 16 },
  row:       { display: 'flex', gap: 16, marginBottom: 0 },
  field:     { marginBottom: 16, flex: 1, minWidth: 0 },
  lbl:       { display: 'block', fontSize: 13, fontWeight: 600, color: '#323130', marginBottom: 6 },
  inp:       { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #c8c6c4', fontSize: 14, color: '#323130', background: '#fff', boxSizing: 'border-box' },
  preview:   { background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 600, color: '#0078D4', marginBottom: 16 },
  success:   { background: '#dff6dd', border: '1px solid #a7e3a5', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#107C10', fontWeight: 600 },
  errBanner: { background: '#fde7e9', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#a4262c' },
  err:       { background: '#fde7e9', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#a4262c', marginBottom: 16 },
  btn:       { background: '#E86E1C', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', borderRadius: 8, border: '1px solid #edebe9', background: '#fff', color: '#605e5c', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  okBtn:     { fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 8, border: '1px solid #a7e3a5', background: '#dff6dd', color: '#107C10', cursor: 'pointer', whiteSpace: 'nowrap' },
  waBtn:     { fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 8, border: '1px solid #86efac', background: '#dcfce7', color: '#166534', cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  iconBtn:   { width: 32, height: 32, borderRadius: 8, border: '1px solid #edebe9', background: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  checkLbl:  { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', userSelect: 'none' },
  tbl:       { width: '100%', borderCollapse: 'collapse' },
  th:        { textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 600, color: '#605e5c', borderBottom: '2px solid #edebe9', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  tr:        { borderBottom: '1px solid #f3f2f1' },
  td:        { padding: '12px 16px', fontSize: 14, color: '#323130', verticalAlign: 'middle' },
  empty:     { fontSize: 14, color: '#a19f9d', textAlign: 'center', padding: 32 },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal:     { background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.25)' },
  closeBtn:  { width: 32, height: 32, borderRadius: 8, border: '1px solid #edebe9', background: '#fff', fontSize: 16, cursor: 'pointer' },
  badgeG:    { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: '#dff6dd', color: '#107C10', whiteSpace: 'nowrap' },
  badgeY:    { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: '#fff4ce', color: '#7A6400', whiteSpace: 'nowrap', display: 'inline-block' },
  badgeGr:   { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: '#f3f2f1', color: '#605e5c', whiteSpace: 'nowrap' },
};
